import React from "react";
import { ErrorMessage, Field, Formik } from "formik";
import { useEffect, useState, useRef } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import * as Yup from "yup";
import { v4 as uuidv4 } from "uuid";

import { ILead } from "@/customTypings/Lead";
import { IAddress } from "../../customTypings/Address";
import { IContact } from "../../customTypings/Contact";
import { showToastSuccess, showToastError } from "../../helpers";

// Custom select components and helper
import FormikSelect, { OptionType } from "../../components/FormikSelect";
import SelectErrorMessage from "../../components/SelectErrorMesage";
import { toCamelCase } from "../../helpers/to-camel-case";

import {
  leadService,
  postcodeService,
  userService,
} from "../../services";
import { ReactSelectBootstrapStyle } from "../../styles/ReactSelectBootstrapStyle";
import { userAtom } from "../../atoms/userAtom";
// Import react-select as ReactSelect for the postcode lookup.
import ReactSelect from "react-select";
import { LoadingSpinner } from "../LoadingSpinner";
import { useAtom } from "@dbeining/react-atom";
import { IAddressOption } from "@/customTypings/AddressOption";

type Props = {
  show: boolean;
  onClose: (updated: boolean) => void;
  leadId: string;
};

const EditLeadModal: React.FC<Props> = ({ show, onClose, leadId }) => {
  const [alertVariant, setAlertVariant] = useState("danger");
  const [currentLead, setCurrentLead] = useState<ILead>();
  const [loading, setLoading] = useState(true);

  // Postcode lookup state.
  const [isLooking, setIsLooking] = useState(false);
  const [lookupOptions, setLookupOptions] = useState<{ value: string; label: string; address: any }[]>([]);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [selectedLookup, setSelectedLookup] = useState<{ value: string; label: string; address: any } | null>(null);
  const [disableAutoOpen, setDisableAutoOpen] = useState(false);
  const lookupSelectRef = useRef<any>(null);

  useEffect(() => {
    if (lookupOptions.length > 0 && lookupSelectRef.current) {
      lookupSelectRef.current.focus();
    }
  }, [lookupOptions]);

  const mapPostcodeResponseToOptions = (response: any): { value: string; label: string; address: any }[] => {
    return response?.delivery_points.map((deliveryPoint: any) => ({
      value: deliveryPoint.udprn,
      label: `${deliveryPoint.line_1}, ${deliveryPoint.line_2}, ${response.town}, ${response.postcode}`,
      address: {
        line1: deliveryPoint.line_1,
        line2: deliveryPoint.line_2 || "",
        town: response.town,
        postcode: response.postcode,
        country: response.country || "UK",
      },
    }));
  };

  // Define the lookUpPostCode function locally.
  const lookUpPostCode = (postcode: string, setStatus: (msg?: string) => void) => {
    if (!postcode.trim()) return;
    setIsLooking(true);
    postcodeService.lookup(postcode).then((res: any) => {
      if (res.delivery_points !== null) {
        const options = mapPostcodeResponseToOptions(res);
        setLookupOptions(options);
        setSelectedLookup(null);
        setMenuIsOpen(true);
        setIsLooking(false);
      } else {
        setLookupOptions([]);
        setMenuIsOpen(false);
        setIsLooking(false);
        setStatus("Postcode lookup failed.");
      }
    });
  };

  const leadSourceOptions: OptionType[] = [
    { value: "Website", label: "Website" },
    { value: "Walk-in", label: "Walk-in" },
    { value: "Telephone", label: "Telephone" },
    { value: "Email", label: "Email" },
  ];

  const contactDepartmentOptions: OptionType[] = [
    { value: "Service", label: "Service" },
    { value: "Online", label: "Online" },
    { value: "Sales", label: "Sales" },
  ];

  // Fetch current lead data when modal is shown.
  useEffect(() => {
    if (show) {
      setLoading(true);
      leadService.get(leadId).then((lead) => {
        setCurrentLead(lead);
        setLoading(false);
      });
    }
  }, [leadId, show]);

  // Fetch personnel options.
  const [allUsers, setAllUsers] = useState<any[]>([]);
  useEffect(() => {
    userService.getAllListItems().then((response) => {
      setAllUsers(response);
    });
  }, []);

  const currentUser = useAtom(userAtom);

  // Build initial form values from currentLead.
  // For email and phone we use currentLead.contact; for address, use currentLead.address.
  const initialValues = currentLead
    ? {
        name: currentLead.name || "",
        email: currentLead.contact?.emailAddress || "",
        phone: currentLead.contact?.mobilePhone || "",
        line1: currentLead.address?.line1 || "",
        line2: currentLead.address?.line2 || "",
        town: currentLead.address?.town || "",
        // The form field is "country" but maps to Address.county when saving.
        country: currentLead.address?.county || "",
        postCode: currentLead.address?.postcode || "",
        isBusiness: currentLead.isBusiness.toString(),
        leadSource: currentLead.leadSource || "",
        contactDepartment: currentLead.contactDepartment || "",
        assignedToPersonnel: currentLead.assignedToPersonnel || "",
      }
    : {
        name: "",
        email: "",
        phone: "",
        line1: "",
        line2: "",
        town: "",
        country: "",
        postCode: "",
        isBusiness: "false",
        leadSource: "",
        contactDepartment: "",
        assignedToPersonnel: "",
      };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Lead Name is required"),
    email: Yup.string().trim().email("Invalid email").required("Email is required"),
    phone: Yup.string()
      .required("Phone Number is required")
      .matches(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/, "Invalid phone number"),
    line1: Yup.string().required("Address Line 1 is required"),
    town: Yup.string().required("Town is required"),
    postCode: Yup.string().required("Postcode is required"),
    country: Yup.string().required("Country is required"),
    assignedToPersonnel: Yup.string()
      .trim()
      .transform((value, originalValue) => (originalValue.trim() === "" ? undefined : value))
      .required("Assigned To Personnel is required"),
    leadSource: Yup.string().trim().required("Lead Source is required"),
    contactDepartment: Yup.string().trim(), // optional
    isBusiness: Yup.boolean(),
  });

  // Assume personnel options are already in OptionType format.
  const personnelOptions: OptionType[] = allUsers;

  const leadTypes = [
    { name: "Domestic", value: false },
    { name: "Business or organisation", value: true },
  ];

  const handleClose = () => onClose(false);

  return (
    <Modal centered show={show} keyboard={false} onHide={handleClose} dialogClassName="customer-modal">
      {loading || !currentLead ? (
        <>
          <Modal.Header closeButton>
            <Modal.Title>Edit Lead</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={3}>Loading...</Col>
              <Col md={9}>
                <LoadingSpinner text="Loading lead data..." />
              </Col>
            </Row>
          </Modal.Body>
        </>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { setStatus, setSubmitting, setFieldError, resetForm }) => {
            setStatus("");
            setSubmitting(true);

            // Build updated Contact object.
            const updatedContact: IContact = {
              id: currentLead.contact?.id,
              name: currentLead.contact?.name || values.name,
              emailAddress: values.email,
              mobilePhone: values.phone,
              workPhone: currentLead.contact?.workPhone,
              email2: currentLead.contact?.email2,
            };

            // Build updated Address object.
            const updatedAddress: IAddress = {
              id: currentLead.address?.id,
              line1: values.line1,
              line2: values.line2 || undefined,
              line3: currentLead.address?.line3 || undefined,
              town: values.town,
              county: values.country, // form's country maps to county
              postcode: values.postCode,
              what3words: currentLead.address?.what3words || "null",
              latitude: currentLead.address?.latitude || "",
              longitude: currentLead.address?.longitude || "",
            };

            // Build updated Lead object.
            const updatedLead: ILead = {
              id: currentLead.id,
              isBusiness: values.isBusiness === "true",
              name: values.name,
              dateOfInquiry: currentLead.dateOfInquiry,
              notes: currentLead.notes,
              contactDepartment: values.contactDepartment,
              identityId: currentLead.identityId,
              assignedToPersonnel: values.assignedToPersonnel || currentUser?.identity.id,
              leadSource: values.leadSource,
              status: currentLead.status,
              leadNumber: currentLead.leadNumber,
              sequence: currentLead.sequence,
              addressId: updatedAddress.id,
              address: updatedAddress,
              contactId: updatedContact.id,
              contact: updatedContact,
            };

            leadService.edit(updatedLead).then(
              (response) => {
                setSubmitting(false);
                if (response.status !== "Failure") {
                  showToastSuccess("Lead updated successfully!");
                  setTimeout(() => onClose(true), 1000);
                } else {
                  setAlertVariant("danger");
                  showToastError(response.message);
                }
              },
              (error: any) => {
                setAlertVariant("danger");
                if (error.status === 400 && error.errors) {
                  Object.keys(error.errors).forEach((key) => {
                    const messages = error.errors[key];
                    if (Array.isArray(messages)) {
                      messages.forEach((message) => showToastError(message));
                    }
                  });
                } else {
                  showToastError("An unexpected error occurred!");
                }
                setSubmitting(false);
              }
            );
          }}
        >
          {({ errors, status, touched, isSubmitting, handleSubmit, values, handleChange, setFieldValue, setFieldError, setFieldTouched, setStatus }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>Edit Lead</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {/* Lead Type */}
                <Form.Group as={Row} className="mb-3" controlId="leadTypeRadios">
                  <Form.Label column md={3}>
                    Lead Type <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9} className="pt-2">
                    {leadTypes.map((leadType) => (
                      <label key={uuidv4()} className="d-block">
                        <Field type="radio" name="isBusiness" value={leadType.value.toString()} className="me-2" required />
                        {leadType.name}
                      </label>
                    ))}
                  </Col>
                </Form.Group>
                {/* Lead Name */}
                <Form.Group as={Row} className="mb-3" controlId="name">
                  <Form.Label column md={3}>
                    Lead Name <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9}>
                    <Field name="name" type="text" className={"form-control" + (errors.name && touched.name ? " is-invalid" : "")} />
                    <ErrorMessage name="name" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                {/* Email */}
                <Form.Group as={Row} className="mb-3" controlId="email">
                  <Form.Label column md={3}>
                    Email <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9}>
                    <Field name="email" type="email" className={"form-control" + (errors.email && touched.email ? " is-invalid" : "")} />
                    <ErrorMessage name="email" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                {/* Phone */}
                <Form.Group as={Row} className="mb-3" controlId="phone">
                  <Form.Label column md={3}>
                    Phone <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9}>
                    <Field name="phone" type="text" className={"form-control" + (errors.phone && touched.phone ? " is-invalid" : "")} />
                    <ErrorMessage name="phone" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                {/* Postcode Lookup for Address */}
                <Form.Group as={Row} controlId="postCode" className="mb-3">
                  <Form.Label column md={3}>
                    Postcode <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9}>
                    <Row className="align-items-center">
                      <Col xs={3} lg={3}>
                        <Field
                          name="postCode"
                          onChange={handleChange}
                          onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (values.postCode && values.postCode.trim()) {
                                lookUpPostCode(values.postCode, setStatus);
                              }
                            }
                          }}
                          type="text"
                          value={values.postCode}
                          className={`form-control ${errors.postCode && touched.postCode ? "is-invalid" : ""}`}
                        />
                        <ErrorMessage name="postCode" component="div" className="invalid-feedback" />
                      </Col>
                      <Col xs={3} lg={2}>
                        <Button
                          variant="primary"
                          onClick={() => {
                            if (values.postCode && values.postCode.trim()) {
                              lookUpPostCode(values.postCode, setStatus);
                            }
                          }}
                          disabled={!values.postCode || !values.postCode.trim() || isLooking}
                        >
                          {isLooking ? (
                            <span style={{ display: "inline-flex", alignItems: "center" }}>
                              <LoadingSpinner text="Looking..." />
                            </span>
                          ) : (
                            "Look Up"
                          )}
                        </Button>
                      </Col>
                      {lookupOptions.length > 0 && (
                        <Col xs={12} lg={7} className="mt-3 mt-lg-0">
                          <div onClick={() => lookupOptions.length > 0 && setMenuIsOpen(true)}>
                            <ReactSelect
                              ref={lookupSelectRef}
                              value={selectedLookup}
                              styles={ReactSelectBootstrapStyle}
                              className="stateManagedSelect"
                              options={lookupOptions}
                              isClearable
                              menuIsOpen={menuIsOpen}
                              onMenuClose={() => setMenuIsOpen(false)}
                              onFocus={() => {
                                if (!disableAutoOpen && lookupOptions.length > 0) {
                                  setMenuIsOpen(true);
                                }
                              }}
                              onChange={(
                                selectedOption: { value: string; label: string; address: IAddressOption } | null
                              ) => {
                                if (!selectedOption) {
                                  setSelectedLookup(null);
                                  return;
                                }
                                const { address } = selectedOption;
                                setFieldValue("line1", toCamelCase(address.line1));
                                setFieldValue("line2", toCamelCase(address.line2));
                                setFieldValue("town", toCamelCase(address.town));
                                setFieldValue("postCode", toCamelCase(address.postcode));
                                setFieldValue("country", address.country);
                                setFieldError("line1", "");
                                setFieldError("town", "");
                                setFieldError("postCode", "");
                                setFieldError("country", "");
                                setFieldTouched("line1", false);
                                setFieldTouched("town", false);
                                setFieldTouched("postCode", false);
                                setFieldTouched("country", false);
                                setSelectedLookup(selectedOption);
                                setMenuIsOpen(false);
                                setDisableAutoOpen(true);
                                setTimeout(() => setDisableAutoOpen(false), 200);
                              }}
                            />
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Form.Group>
                {/* Address Fields */}
                <Form.Group as={Row} controlId="line1" className="mb-3">
                  <Form.Label column md={3}>
                    Address Line 1 <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9}>
                    <Field
                      name="line1"
                      onChange={handleChange}
                      type="text"
                      value={values.line1}
                      className={`form-control ${errors.line1 && touched.line1 ? "is-invalid" : ""}`}
                    />
                    <ErrorMessage name="line1" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="line2" className="mb-3">
                  <Form.Label column md={3}>
                    Address Line 2
                  </Form.Label>
                  <Col md={9}>
                    <Field
                      name="line2"
                      onChange={handleChange}
                      type="text"
                      value={values.line2}
                      className={`form-control ${errors.line2 && touched.line2 ? "is-invalid" : ""}`}
                    />
                    <ErrorMessage name="line2" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="town" className="mb-3">
                  <Form.Label column md={3}>
                    Town <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9}>
                    <Field
                      name="town"
                      onChange={handleChange}
                      type="text"
                      value={values.town}
                      className={`form-control ${errors.town && touched.town ? "is-invalid" : ""}`}
                    />
                    <ErrorMessage name="town" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="country" className="mb-3">
                  <Form.Label column md={3}>
                    Country <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9}>
                    <Field
                      name="country"
                      onChange={handleChange}
                      type="text"
                      value={values.country}
                      className={`form-control ${errors.country && touched.country ? "is-invalid" : ""}`}
                    />
                    <ErrorMessage name="country" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                {/* Lead Source */}
                <Form.Group as={Row} controlId="leadSource" className="mb-3">
                  <Form.Label column md={3}>
                    Lead Source <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9}>
                    <FormikSelect
                      styles={ReactSelectBootstrapStyle}
                      className="stateManagedSelect"
                      name="leadSource"
                      isClearable
                      options={leadSourceOptions}
                    />
                    <SelectErrorMessage name="leadSource" />
                  </Col>
                </Form.Group>
                {/* Contact Department */}
                <Form.Group as={Row} controlId="contactDepartment" className="mb-3">
                  <Form.Label column md={3}>
                    Contact Department <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9}>
                    <FormikSelect
                      styles={ReactSelectBootstrapStyle}
                      className="stateManagedSelect"
                      name="contactDepartment"
                      isClearable
                      options={contactDepartmentOptions}
                    />
                    <SelectErrorMessage name="contactDepartment" />
                  </Col>
                </Form.Group>
                {/* Assigned To Personnel */}
                <Form.Group as={Row} controlId="assignedToPersonnel" className="mb-3">
                  <Form.Label column md={3}>
                    Assigned To Personnel <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col md={9}>
                    <FormikSelect
                      styles={ReactSelectBootstrapStyle}
                      className="stateManagedSelect"
                      name="assignedToPersonnel"
                      isClearable
                      options={personnelOptions}
                    />
                    <SelectErrorMessage name="assignedToPersonnel" />
                  </Col>
                </Form.Group>
                {status && (
                  <Alert variant={alertVariant} className="mt-3">
                    {status}
                  </Alert>
                )}
              </Modal.Body>
              <Modal.Footer>
                <div className="form-group">
                  <Button variant="primary" disabled={isSubmitting} type="submit" className="me-2">
                    {isSubmitting ? <LoadingSpinner text="Updating lead..." /> : "Update lead"}
                  </Button>
                  <Button variant="secondary" onClick={handleClose}>
                    Cancel
                  </Button>
                </div>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
};

export { EditLeadModal };